import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { TrendingUp, ArrowRight, Loader2, AlertTriangle, Lightbulb, BarChart3, Layers, FileText, X, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, Cell, Legend, ReferenceLine } from "recharts";
import { Shimmer } from "../common/DashboardSkeleton";
import { usePrefetchedData } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SIGNAL_COLORS = { untapped: "#f59e0b", "over-productized": "#ef4444", balanced: "#10b981" };
const SIGNAL_LABELS = {
  untapped: "Untapped Potential — high research density, few products",
  "over-productized": "Over-Productized — many products, little research backing",
  balanced: "Balanced — research and product density roughly aligned",
};

/* ─── Reveal ─── */
const Reveal = ({ children, delay = 0 }) => {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s ease ${delay}ms` }}>
      {children}
    </div>
  );
};

export const ResearchGapAnalysis = () => {
  const prefetched = usePrefetchedData();
  const [industryGap, setIndustryGap] = useState(null);
  const [countryGap, setCountryGap] = useState(null);
  const [industryLoading, setIndustryLoading] = useState(true);
  const [countryLoading, setCountryLoading] = useState(true);
  const [briefIndustry, setBriefIndustry] = useState(null);
  const [briefData, setBriefData] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);

  useEffect(() => {
    if (prefetched.researchIndustryGap && !industryGap) { setIndustryGap(prefetched.researchIndustryGap); setIndustryLoading(false); }
    if (prefetched.researchGap && !countryGap) { setCountryGap(prefetched.researchGap); setCountryLoading(false); }
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!industryGap) {
        axios.get(`${API}/research/industry-gap`)
          .then(r => setIndustryGap(prev => prev || r.data))
          .catch(() => {})
          .finally(() => setIndustryLoading(false));
      }
      if (!countryGap) {
        axios.post(`${API}/research/gap-analysis`)
          .then(r => setCountryGap(prev => prev || r.data))
          .catch(() => {})
          .finally(() => setCountryLoading(false));
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generateBrief = async (industry) => {
    setBriefIndustry(industry);
    setBriefData(null);
    setBriefLoading(true);
    try {
      const res = await axios.post(`${API}/research/industry-brief`, { industry });
      setBriefData(res.data);
    } catch (e) {
      setBriefData({ brief: "Failed to generate brief. Please try again.", error: true });
    }
    setBriefLoading(false);
  };

  if (industryLoading && countryLoading && !industryGap && !countryGap) {
    return (
      <div className="space-y-5 animate-fade-in" data-testid="gap-analysis-loading">
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 space-y-3">
          <Shimmer className="h-3 w-52" />
          <Shimmer className="h-6 w-80" />
          <Shimmer className="h-3 w-64" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[1, 2].map(i => (
            <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
              <Shimmer className="h-3 w-40 mb-4" />
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(j => (
                  <div key={j} className="flex items-center gap-3">
                    <Shimmer className="w-24 h-3" />
                    <Shimmer className="flex-1 h-4 rounded-full" />
                    <Shimmer className="w-10 h-3" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-[10px] text-slate-500 animate-pulse">Analyzing research-product gaps...</p>
      </div>
    );
  }

  const scatterData = (industryGap?.gaps || []).map(g => ({
    ...g,
    x: g.products,
    y: g.research_papers,
    name: g.industry.replace(" & ", "\n& "),
  }));

  const careGapData = (industryGap?.gaps || []).map(g => ({
    ...g,
    name: g.industry.length > 18 ? g.industry.slice(0, 18) + "..." : g.industry,
    fullName: g.industry,
  })).sort((a, b) => b.care_gap - a.care_gap);

  return (
    <div className="space-y-6" data-testid="research-gap-analysis">
      {/* Header */}
      <Reveal>
        <div className="relative overflow-hidden rounded-2xl py-8 px-6 text-center"
          style={{ background: "linear-gradient(135deg, #1a0a28 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(245,158,11,0.15)" }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-24 bg-amber-500/[0.04] rounded-full blur-3xl" />
          <p className="text-[10px] font-bold text-amber-400/70 uppercase tracking-[0.25em] mb-3">Innovation Intelligence</p>
          <h1 className="text-xl md:text-2xl font-bold text-white mb-2">Research vs. Product Gap Analysis</h1>
          <p className="text-sm text-slate-400 max-w-xl mx-auto">
            Where is academic research outpacing product development? Where are products being built without research backing?
          </p>
        </div>
      </Reveal>

      {/* Scatter: Research Papers vs Products by Industry */}
      {scatterData.length > 0 && (
        <Reveal delay={100}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="w-4 h-4 text-amber-400" />
              <p className="text-[10px] font-bold text-amber-400/70 uppercase tracking-[0.2em]">Research Density vs Product Density</p>
            </div>
            <p className="text-[10px] text-slate-500 mb-4">Each bubble is an industry. Above the diagonal = more research than products. Below = more products than research.</p>
            <ResponsiveContainer width="100%" height={350}>
              <ScatterChart margin={{ top: 20, right: 20, bottom: 30, left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis type="number" dataKey="x" name="Products" tick={{ fill: "#64748b", fontSize: 10 }}
                  label={{ value: "Products", position: "bottom", fill: "#64748b", fontSize: 10 }} />
                <YAxis type="number" dataKey="y" name="Research Papers" tick={{ fill: "#64748b", fontSize: 10 }}
                  label={{ value: "Research Papers", angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }}
                  formatter={(val, name) => [val.toLocaleString(), name]}
                  labelFormatter={(_val, payload) => payload?.[0]?.payload?.industry || ""} />
                <Scatter data={scatterData} fill="#8b5cf6">
                  {scatterData.map((entry) => (
                    <Cell key={entry.industry} fill={SIGNAL_COLORS[entry.signal] || "#6366f1"} r={Math.max(6, Math.min(18, entry.products / 500))} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-3">
              {Object.entries(SIGNAL_COLORS).map(([key, color]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full" style={{ background: color }} />
                  <span className="text-[9px] text-slate-500 capitalize">{key}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* Industry Gap Cards */}
      {(industryGap?.gaps || []).length > 0 && (
        <Reveal delay={150}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-4 h-4 text-amber-400" />
              <p className="text-[10px] font-bold text-amber-400/70 uppercase tracking-[0.2em]">Industry Innovation Signals</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {industryGap.gaps.map((g, i) => (
                <Reveal key={g.industry} delay={i * 60}>
                  <div className="bg-white/[0.01] border rounded-xl p-4 hover:bg-white/[0.03] transition-all"
                    style={{ borderColor: `${SIGNAL_COLORS[g.signal]}20` }}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-bold text-white truncate flex-1">{g.industry}</h3>
                      <span className="px-2 py-0.5 rounded-full text-[8px] font-bold uppercase ml-2"
                        style={{ background: `${SIGNAL_COLORS[g.signal]}15`, color: SIGNAL_COLORS[g.signal] }}>
                        {g.signal}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <p className="text-lg font-black text-cyan-400">{g.research_papers.toLocaleString()}</p>
                        <p className="text-[8px] text-slate-500">Research Papers</p>
                      </div>
                      <div>
                        <p className="text-lg font-black text-indigo-400">{g.products.toLocaleString()}</p>
                        <p className="text-[8px] text-slate-500">Products</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-white/[0.04]">
                      <span className="text-[9px] text-slate-500">Ratio: <span className="text-white font-bold">{g.ratio}x</span></span>
                      <span className="text-[9px] text-slate-500">Care gap: <span className={g.care_gap > 0 ? "text-emerald-400" : "text-red-400"} style={{ fontWeight: 700 }}>{g.care_gap > 0 ? "+" : ""}{g.care_gap}%</span></span>
                    </div>
                    <p className="text-[8px] text-slate-500 mt-1.5">{SIGNAL_LABELS[g.signal]}</p>
                    <button onClick={(e) => { e.stopPropagation(); generateBrief(g.industry); }}
                      className="w-full mt-2.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 rounded-lg text-[9px] text-amber-300 font-bold transition-all flex items-center justify-center gap-1.5 group"
                      data-testid={`brief-btn-${i}`}>
                      <Sparkles className="w-3 h-3 group-hover:animate-pulse" />
                      Intelligence Brief
                    </button>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      {/* Care Gap by Industry (bar chart) */}
      {careGapData.length > 0 && (
        <Reveal delay={200}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-emerald-400" />
              <p className="text-[10px] font-bold text-emerald-400/70 uppercase tracking-[0.2em]">Care Orientation Gap: Research vs Products</p>
            </div>
            <p className="text-[10px] text-slate-500 mb-4">Positive = research is more care-oriented than products. Negative = products are more care-oriented.</p>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={careGapData} layout="vertical" margin={{ left: 100, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 9 }} width={95} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }}
                  formatter={(val) => [`${val > 0 ? "+" : ""}${val}%`, "Care Gap"]}
                  labelFormatter={(_val, payload) => payload?.[0]?.payload?.fullName || ""} />
                <ReferenceLine x={0} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
                <Bar dataKey="care_gap" radius={[0, 4, 4, 0]} name="Care Gap">
                  {careGapData.map((entry) => (
                    <Cell key={entry.industry || entry.fullName} fill={entry.care_gap > 0 ? "#10b981" : "#ef4444"} fillOpacity={0.6} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Reveal>
      )}

      {/* Country Gap Analysis */}
      {countryGap?.gaps?.length > 0 && (
        <Reveal delay={250}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-4 h-4 text-violet-400" />
              <p className="text-[10px] font-bold text-violet-400/70 uppercase tracking-[0.2em]">Country-Level Care Gap: Research vs Products</p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={countryGap.gaps.slice(0, 15)} layout="vertical" margin={{ left: 80, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis type="category" dataKey="country" tick={{ fill: "#94a3b8", fontSize: 10 }} width={75} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="research_care_pct" fill="#10b981" name="Research Care %" radius={[0, 2, 2, 0]} />
                <Bar dataKey="product_care_pct" fill="#3b82f6" name="Product Care %" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Reveal>
      )}

      {/* Intelligence Brief Modal */}
      {briefIndustry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { setBriefIndustry(null); setBriefData(null); }}>
          <div className="relative w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto bg-[#0b1222] border border-amber-500/20 rounded-2xl shadow-2xl"
            style={{ boxShadow: "0 0 60px rgba(245,158,11,0.08)" }}
            onClick={e => e.stopPropagation()} data-testid="brief-modal">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[#0b1222] border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-[9px] text-amber-400/70 uppercase tracking-[0.2em] font-bold">Intelligence Brief</p>
                  <p className="text-sm font-bold text-white">{briefIndustry}</p>
                </div>
              </div>
              <button onClick={() => { setBriefIndustry(null); setBriefData(null); }}
                className="p-2 rounded-lg hover:bg-white/[0.05] transition-colors" data-testid="brief-close">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <div className="px-6 py-5">
              {briefLoading ? (
                <div className="flex flex-col items-center py-12 gap-3">
                  <div className="relative">
                    <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
                    <Sparkles className="w-4 h-4 text-amber-300 absolute -top-1 -right-1 animate-pulse" />
                  </div>
                  <p className="text-sm text-slate-400">Analyzing research-product dynamics...</p>
                  <p className="text-[10px] text-slate-500">Claude is reviewing the data patterns</p>
                </div>
              ) : briefData ? (
                <div className="space-y-5" style={{ animation: "revealUp 0.5s ease-out" }}>
                  {/* Stats ribbon */}
                  {briefData.stats && (
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
                      {[
                        { label: "Papers", value: briefData.stats.research_papers?.toLocaleString(), color: "#06b6d4" },
                        { label: "Products", value: briefData.stats.products?.toLocaleString(), color: "#8b5cf6" },
                        { label: "Ratio", value: `${briefData.stats.ratio}x`, color: "#f59e0b" },
                        { label: "Research Care", value: `${briefData.stats.research_care_pct}%`, color: "#10b981" },
                        { label: "Product Care", value: `${briefData.stats.product_care_pct}%`, color: "#3b82f6" },
                        { label: "Care Gap", value: `${briefData.stats.care_gap > 0 ? "+" : ""}${briefData.stats.care_gap}%`, color: briefData.stats.care_gap > 0 ? "#10b981" : "#ef4444" },
                      ].map((s) => (
                        <div key={s.label} className="text-center p-2 bg-white/[0.02] rounded-lg border border-white/[0.04]">
                          <p className="text-sm font-black" style={{ color: s.color }}>{s.value}</p>
                          <p className="text-[7px] text-slate-500 uppercase">{s.label}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Brief narrative */}
                  <div className="prose prose-invert prose-sm max-w-none">
                    {briefData.brief.split("\n\n").map((para, i) => (
                      <p key={`brief-${i}`} className="text-[11px] text-slate-300 leading-relaxed mb-3"
                        style={{ animation: `revealUp 0.4s ease-out ${i * 0.1}s both` }}>
                        {para}
                      </p>
                    ))}
                  </div>
                  <div className="pt-3 border-t border-white/[0.04] text-center">
                    <p className="text-[8px] text-slate-600">Generated by Claude · AI Orientation Observatory</p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
